import type { SearchResult, SmartSearchResponse } from "../types/search";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSearch } from "../context/SearchContext";
import {
  Loader2,
  Download,
  Copy,
  Filter,
  ChevronLeft,
  ChevronRight,
  Info,
} from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { toRootDomain } from '../utils/domain';
import { getLinkedInProspects } from "../api";
import ProspectsDrawer from "./ProspectsDrawer";
import type { LinkedInProspectResponse } from "../types/prospects";

function cleanDomain(d: string): string {
  try {
    const u = new URL(d.includes("://") ? d : `http://${d}`);
    return u.hostname.toLowerCase();
  } catch {
    return "";
  }
}

function formatIndustry(industry?: string | string[]): string {
  if (Array.isArray(industry)) {
    return industry.length > 0 ? industry.join("; ") : "NA";
  }
  return industry && industry.trim() !== "" ? industry : "NA";
}



type SearchProps = {
  // Optional: when provided, enables the "LinkedIn Prospects" button per row
  onOpenProspects?: (domain: string) => void;
};

const PAGE_SIZE = 10;
const MAX_RESULTS = 100;
const MAX_PAGES = MAX_RESULTS / PAGE_SIZE; // = 10


export default function Search({ onOpenProspects }: SearchProps) {
  const { token, user, setUser, refreshUser } = useAuth();
  const { query, setQuery, exclude, setExclude, page, setPage, results, setResults } = useSearch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [smartFilter, setSmartFilter] = useState("");
  const isSmartMode = !!smartFilter;
  const [brandUrlContains, setBrandUrlContains] = useState("");
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
type SearchOperator =
  | "none"
  | "exact"
  | "intitle"
  | "inurl"
  | "site"
  | "exclude_site"
  | "filetype";

type JoinOperator = "AND" | "OR";

type OperatorRow = {
  id: number;
  value: string;
  operator: SearchOperator;
  join: JoinOperator;
};

const [operatorRows, setOperatorRows] = useState<OperatorRow[]>([
  { id: 1, value: "", operator: "none", join: "AND" },
]);

const buildBroadQuery = () => {
  const formatRow = (row: OperatorRow) => {
    const value = row.value.trim();
    if (!value) return "";

    switch (row.operator) {
      case "exact":
        return `"${value.replace(/^"+|"+$/g, "")}"`;

      case "intitle":
        return `intitle:"${value.replace(/^"+|"+$/g, "")}"`;

      case "inurl":
        return `inurl:${value
          .replace(/^https?:\/\//i, "")
          .replace(/\s+/g, "")}`;

      case "site":
        return `site:${cleanDomain(value)}`;

      case "exclude_site":
        return `-site:${cleanDomain(value)}`;

      case "filetype":
        return `filetype:${value
          .replace(/^\./, "")
          .replace(/\s+/g, "")}`;

      default:
        return value;
    }
  };

  const activeRows = operatorRows
    .map((row) => ({
      ...row,
      formatted: formatRow(row),
    }))
    .filter((row) => row.formatted);

  if (!activeRows.length) return "";

  const groups: string[] = [];
  let currentGroup: string[] = [activeRows[0].formatted];

  for (let i = 1; i < activeRows.length; i += 1) {
    const row = activeRows[i];

    if (row.join === "OR") {
      currentGroup.push(row.formatted);
    } else {
      groups.push(
        currentGroup.length > 1
          ? `(${currentGroup.join(" OR ")})`
          : currentGroup[0]
      );

      currentGroup = [row.formatted];
    }
  }

  groups.push(
    currentGroup.length > 1
      ? `(${currentGroup.join(" OR ")})`
      : currentGroup[0]
  );

  return groups.join(" AND ");
};


  // derived array for preview
const excludeList = exclude
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);


  // Initialize credits from user, then sync on user changes
  const [credits, setCredits] = useState<number | null>(user?.credits ?? null);
  useEffect(() => {
    refreshUser();
  }, []);
  useEffect(() => {
    setCredits(user?.credits ?? null);
  }, [user]);

useEffect(() => {
  setQuery("");
  setBrandUrlContains("");
  setOperatorRows([
    { id: Date.now(), value: "", operator: "none", join: "AND" },
  ]);
  setResults([]);
  setCachedPages({});
  setPage(1);
}, [smartFilter]);

useEffect(() => {
  if (isSmartMode) setPage(1);
}, [smartFilter]);

  const isMobile = useIsMobile();
  const [cachedPages, setCachedPages] = useState<Record<number, SearchResult[]>>({});
  const [showCopied, setShowCopied] = useState(false);
  const [showTip, setShowTip] = useState(false);

  // Drawer state
const [drawerOpen, setDrawerOpen] = useState(false);
const [prospectsData, setProspectsData] = useState<LinkedInProspectResponse | null>(null);
const [prospectsLoading, setProspectsLoading] = useState(false);
const [outreachLoading, setOutreachLoading] = useState<Record<string, boolean>>({});
const [bulkOutreachLoading, setBulkOutreachLoading] = useState(false);
const [prospectsError, setProspectsError] = useState<string | null>(null);

const [expandedOutreach, setExpandedOutreach] = useState<Record<string, boolean>>({});

  // --- Reset query/results on login ---
  useEffect(() => {
    if (token) {
      // Clear stale search state whenever user logs in
      setQuery("");
      setExclude("");
      setResults([]);
      setPage(1);
      setCachedPages({});
    }
  }, [token]);

  // --- Clear on hard refresh ---
  useEffect(() => {
    // Run only once on component mount
    setQuery("");
    setExclude("");
    setResults([]);
    setPage(1);
    setCachedPages({});
  }, []);


// --- Fetch Results ---
const fetchResults = async (
  queryArg: string,
  excludeArg: string,
  pageArg: number
) => {
  if (!queryArg.trim()) return;

  // ── CLIENT-SIDE PAGE BOUNDS CHECK ────────────────────────────────────────
  if (pageArg < 1 || pageArg > MAX_PAGES) {
    setError(
      `Page ${pageArg} is out of range. You can only view up to ${MAX_PAGES} pages (${MAX_RESULTS} results).`
    );
    return;
  }

  setError(null);
  setLoading(true);

  try {
    let response;

    if (smartFilter) {
      // --- Smart filter path (GET) ---
      // Include user-provided excluded domains dynamically
      const excludeParams = excludeArg
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean)
        .map((e) => `exclude=${encodeURIComponent(e)}`)
        .join("&");

const endpoint = `${process.env.REACT_APP_BACKEND_URL}/search/smart?filter=${smartFilter}&query=${encodeURIComponent(
  queryArg
)}${excludeParams ? `&${excludeParams}` : ""}&page=${pageArg}`;


      response = await fetch(endpoint, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      // --- Normal search path (POST) ---
      response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/search`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: queryArg,
          exclude: excludeArg
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          page: pageArg,
        }),
      });
    }

if (!response.ok) {
  const text = await response.text();

  // ✅ Detect credit exhaustion (HTTP 402)
  if (response.status === 402 || text.toLowerCase().includes("credit")) {
    setError("You’re out of credits. Please upgrade to continue.");
    setHasNext(false);
    setLoading(false);
    // Disable pagination and searching until user refreshes or tops up
    setCredits(0);
    if (user) setUser({ ...user, credits: 0 });
    return;
  }

  throw new Error(`Error ${response.status}: ${text || "Failed to search"}`);
}

    const data: SmartSearchResponse = await response.json();
    const results = data.results || [];
    setResults(results);

if (isSmartMode) {
  setHasNext(data.has_next);
  setHasPrev(data.has_prev);
}


    // Cache this page for faster re-navigation
    setCachedPages((prev) => ({ ...prev, [pageArg]: results }));

    // ✅ Unified credit update (works for both normal & smart searches)
    if (typeof data.credits_left === "number") {
      setCredits(data.credits_left);
      if (user) setUser({ ...user, credits: data.credits_left });
    }
  } catch (e: any) {
    setError(e.message || "Something went wrong");
  } finally {
    setLoading(false);
  }
};


// --- Search Validation ---
function isValidQuery(q: string) {
  const t = q.trim();

  if (!t) return "Enter a search term.";

  if (t.length < 2) {
    return "Search term is too short.";
  }

  return true;
}


function hasBrokenOperators(q: string): string | false {
  if (/site:\s*($|\s)/i.test(q))
    return "You used 'site:' but didn't specify a domain.";
  if (/(intitle:|inurl:|intext:)\s*($|\s)/i.test(q))
    return "Your operator is missing a keyword.";
  const quotes = (q.match(/"/g) || []).length;
  if (quotes % 2 !== 0)
    return 'You have an unmatched quote ("). Please add the closing quote.';
  return false;
}


const handleSubmit = (e: React.FormEvent) => {
  e.preventDefault();

  let finalQuery = smartFilter ? query.trim() : buildBroadQuery();

if (smartFilter === "unlinked_mentions" && brandUrlContains.trim()) {
  const urlPart = brandUrlContains
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^inurl:/i, "")
    .replace(/\s+/g, "");

  if (urlPart) {
    finalQuery = `${query.trim()} inurl:${urlPart}`;
  }
}

  const valid = isValidQuery(finalQuery);
  if (valid !== true) {
    setError(typeof valid === "string" ? valid : "Invalid search query");
    return;
  }

  const broken = hasBrokenOperators(finalQuery);
  if (broken) {
    setError(broken);
    return;
  }

  // Keep the generated Broad Search query in the existing search state.
  if (!smartFilter) {
    setQuery(finalQuery);
  }

  setError(null);
  setCachedPages({});
  setHasNext(false);
  setHasPrev(false);

  if (page !== 1) {
    setResults([]);
    setPage(1);
  } else {
    fetchResults(finalQuery, exclude, 1);
  }
};

// --- Pagination Effect (only if results exist already) ---
useEffect(() => {
  if (!query.trim()) return;

  let paginationQuery = query.trim();

  if (smartFilter === "unlinked_mentions" && brandUrlContains.trim()) {
    const urlPart = brandUrlContains
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/^inurl:/i, "")
      .replace(/\s+/g, "");

    if (urlPart) {
      paginationQuery = `${query.trim()} inurl:${urlPart}`;
    }
  }

  fetchResults(paginationQuery, exclude, page);
}, [page]);



  // --- CSV Download ---
function downloadCSV() {
  if (!results.length) return;
  const header = ["Title", "URL", "DA", "PA", "Spam Score", "Description", "Industry"];
  const rows = results.map((r) => [
    r.title,
    r.url,
    r.da,
    r.pa,
    r.spam_score,
    r.description || "-",
    formatIndustry(r.industry)   // ✅ safe
  ]);

  const csv = [header, ...rows]
    .map((row) => row.map((c) => `"${c}"`).join(","))
    .join("\r\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "results.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}



  // --- Copy URLs ---
async function copyAllUrls() {
  if (!results.length) return;

  const header = ["Title", "URL", "DA", "PA", "Spam Score", "Description", "Industry"];
  const rows = results.map((r) =>
    [
      r.title,
      r.url,
      r.da,
      r.pa,
      r.spam_score,
      r.description || "-",
      formatIndustry(r.industry)   // ✅ safe
    ].join("\t") // tab separator for Excel/Sheets
  );

  const text = [header.join("\t"), ...rows].join("\n");
  await navigator.clipboard.writeText(text);

  setShowCopied(true);
  setTimeout(() => setShowCopied(false), 1400);
}

async function handleFindOutreach(domain: string) {
  try {
    setOutreachLoading((prev) => ({
      ...prev,
      [domain]: true,
    }));

    const applyOutreachData = (outreachData: any) => {
      setResults((prev) =>
        prev.map((r) =>
          toRootDomain(r.url) === domain
            ? {
                ...r,
                outreach_pages: outreachData.outreach_pages || [],
                emails: outreachData.emails || [],
                email_contacts: outreachData.email_contacts || [],
                has_form: Boolean(outreachData.has_form),
                form_url: outreachData.form_url || null,
                outreach_checked: true,
              }
            : r
        )
      );
    };

    // Start enrichment. If fresh metadata is already cached,
    // the backend may return it immediately.
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/outreach/enrich`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain }),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        `Outreach request failed: ${response.status} ${text}`
      );
    }

    const data = await response.json();

    // Backend currently uses "complete".
    // Keep "ready" accepted for compatibility.
    if (
      (data.status === "complete" || data.status === "ready") &&
      data.data
    ) {
      applyOutreachData(data.data);
      return;
    }

    // Otherwise Celery is processing the domain.
    // Poll until metadata becomes available.
    for (let attempt = 0; attempt < 20; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const pollResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/outreach/${encodeURIComponent(
          domain
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!pollResponse.ok) {
        continue;
      }

      const pollData = await pollResponse.json();

      if (
        (pollData.status === "complete" ||
          pollData.status === "ready") &&
        pollData.data
      ) {
        applyOutreachData(pollData.data);
        return;
      }
    }

    throw new Error("Outreach enrichment timed out");
  } catch (err) {
    console.error("Outreach enrichment failed:", err);
  } finally {
    setOutreachLoading((prev) => {
      const next = { ...prev };
      delete next[domain];
      return next;
    });
  }
}

async function handleFindAllOutreach() {
  const domains = Array.from(
    new Set(
      results
        .filter(
          (site) =>
            !site.outreach_checked &&
            !site.outreach_pages?.length &&
            !site.emails?.length &&
            !site.has_form
        )
        .map((site) => toRootDomain(site.url) || site.url)
        .filter(Boolean)
    )
  );

if (!domains.length) return;

setBulkOutreachLoading(true);

try {
  for (const domain of domains) {
    await handleFindOutreach(domain);
  }
} finally {
  setBulkOutreachLoading(false);
}
}


  async function handleOpenProspects(domain: string) {
  setDrawerOpen(true);
  setProspectsLoading(true);
  setProspectsError(null);
  setProspectsData(null);

  try {
    const res = await getLinkedInProspects(domain);
    setProspectsData(res);
  } catch (err: any) {
    setProspectsError(err.message || "Failed to load prospects");
  } finally {
    setProspectsLoading(false);
  }
}


  // --- Result Range ---
  const startIndex = (page - 1) * PAGE_SIZE + 1;
  const endIndex = startIndex + results.length - 1;

  // --- Dynamic placeholder and tooltip based on Smart Filter ---
const getQueryPlaceholder = () => {
  switch (smartFilter) {
    case "unlinked_mentions":
      return "Brand or phrase, e.g. Credo AI";
    case "comparison_alternatives":
      return "Brand or competitor, e.g. Credo AI";
    case "competitor_mentions":
      return "Competitor brand, e.g. OneTrust";
    case "directories":
      return "Topic or industry, e.g. AI governance";
    case "get_listed":
      return "Product category or niche, e.g. AI governance";
    case "tool_aggregators":
      return "Topic or category, e.g. AI governance";
    case "listicles":
      return "Topic or category, e.g. AI governance";
    default:
      return "Enter a search term";
  }
};

const getQueryHint = () => {
  switch (smartFilter) {
    case "unlinked_mentions":
      return "Find third-party pages mentioning a brand or exact phrase.";
    case "comparison_alternatives":
      return "Find pages comparing a brand with competitors or alternatives.";
    case "competitor_mentions":
      return "Find third-party pages mentioning a competitor.";
    case "directories":
      return "Find relevant directories and catalogs in your niche.";
    case "get_listed":
      return "Find pages where you can submit or list a product, company, tool, or resource.";
    case "tool_aggregators":
      return "Find articles and collections that round up tools or software.";
    case "listicles":
      return "Find Best, Top and similar list-style articles.";
    default:
      return "Search for backlink opportunities.";
  }
};

const isQueryDisabled = false; // all current smart filters require query input



return (
  <div className="w-full flex flex-col items-center py-6 px-4 bg-gray-50 min-h-screen">
    
    {/* ─────────── SEARCH FORM CARD ─────────── */}
    <div className="w-full max-w-4xl bg-white rounded-2xl border border-gray-200 shadow-sm px-6 sm:px-8 py-7">
      {/* ── 100-result cap banner ─────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
            <Filter className="w-5 h-5 text-blue-600" />
          </div>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Backlink Discovery
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Find relevant sites, articles and outreach opportunities.
            </p>
          </div>
        </div>

        {credits !== null && (
          <div className="flex items-center gap-2">
            <span className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap">
              {credits} credits
            </span>

            {credits <= 0 && (
              <span className="bg-red-50 text-red-700 px-3 py-1.5 rounded-full text-xs font-semibold">
                Out of credits
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mb-6 text-xs text-gray-500 bg-gray-50 border border-gray-100 px-4 py-3 rounded-xl">
        Up to {MAX_RESULTS} results per search | {MAX_PAGES} pages | {PAGE_SIZE} results per page
      </div>

      {/* No Credits Message */}
      {credits !== null && credits <= 0 && (
        <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6 text-center shadow font-semibold">
          <b>You’re out of credits!</b>
          <br />
          <span>Please top up to continue searching.</span>
          <br />
          <Link
            to="/buy-credits"
            className="mt-2 inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold"
          >
            Buy Credits
          </Link>
        </div>
      )}

      {/* Search Form */}
      <form className="space-y-6 mb-6" onSubmit={handleSubmit}>
        {/* Smart Filter Selector */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-gray-800">
            Search mode
          </label>
          <select
            value={smartFilter}
            onChange={(e) => setSmartFilter(e.target.value)}
            className="w-full border border-gray-200 px-4 py-3.5 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 transition text-gray-800 placeholder-gray-400"
          >
<option value="">Broad Search</option>
<option value="listicles">Best / Top Lists</option>
<option value="tool_aggregators">Tool Roundups</option>
<option value="unlinked_mentions">Brand Mentions</option>
<option value="comparison_alternatives">Competitor Comparisons</option>
<option value="competitor_mentions">Competitor Mentions</option>
<option value="directories">Directories</option>
<option value="get_listed">Get Listed</option>
          </select>
        </div>

{/* Query Input / Broad Search Builder */}
{smartFilter ? (
  <div>
    <label className="block font-semibold mb-2 text-gray-800">
      Search Query
    </label>

    <input
      type="text"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      className="w-full border border-gray-200 px-4 py-3.5 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 transition text-gray-800 placeholder-gray-400"
      placeholder={getQueryPlaceholder()}
      disabled={credits !== null && credits <= 0}
    />

    <p className="text-xs text-gray-500 mt-1">
      {getQueryHint()}
    </p>

    {smartFilter === "unlinked_mentions" && (
      <div className="mt-4">
        <label className="block font-semibold mb-2 text-gray-800">
          URL contains
          <span className="ml-1 text-xs font-normal text-gray-500">
            (optional)
          </span>
        </label>

        <input
          type="text"
          value={brandUrlContains}
          onChange={(e) => setBrandUrlContains(e.target.value)}
          placeholder="blog, article, news, insights..."
          className="w-full border border-gray-200 px-4 py-3.5 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 transition text-gray-800 placeholder-gray-400"
          disabled={credits !== null && credits <= 0}
        />

        <p className="text-xs text-gray-500 mt-1">
          Leave blank to search all pages. For example, enter blog to find
          brand mentions on URLs containing "blog".
        </p>
      </div>
    )}
  </div>
) : (
  <div>
    <div className="mb-3">
      <label className="block font-semibold text-gray-800">
        Build your search
      </label>

      <p className="text-xs text-gray-500 mt-1">
        Add conditions and combine them with AND or OR. We handle the search syntax automatically.
      </p>
    </div>

    <div className="space-y-3">
      {operatorRows.map((row, index) => (
        <div
          key={row.id}
          className="flex flex-col sm:flex-row sm:items-center gap-2"
        >
          {index > 0 && (
            <select
              value={row.join}
              onChange={(e) => {
                const join = e.target.value as JoinOperator;

                setOperatorRows((rows) =>
                  rows.map((item) =>
                    item.id === row.id
                      ? { ...item, join }
                      : item
                  )
                );
              }}
              className="sm:w-20 border border-blue-100 px-2 py-3 rounded-xl bg-blue-50 text-blue-700 focus:bg-white focus:ring-2 focus:ring-blue-500 font-semibold text-sm"
              disabled={credits !== null && credits <= 0}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          )}

          <input
            type="text"
            value={row.value}
            onChange={(e) => {
              const value = e.target.value;

              setOperatorRows((rows) =>
                rows.map((item) =>
                  item.id === row.id
                    ? { ...item, value }
                    : item
                )
              );
            }}
            className="flex-1 border border-gray-200 px-4 py-3 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 transition"
            placeholder={
              index === 0
                ? "e.g. AI governance solutions"
                : "Add another keyword, domain or value"
            }
            disabled={credits !== null && credits <= 0}
          />

          <select
            value={row.operator}
            onChange={(e) => {
              const operator = e.target.value as SearchOperator;

              setOperatorRows((rows) =>
                rows.map((item) =>
                  item.id === row.id
                    ? { ...item, operator }
                    : item
                )
              );
            }}
            className="sm:w-44 border border-gray-200 px-3 py-3 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500"
            disabled={credits !== null && credits <= 0}
          >
            <option value="none">None</option>
            <option value="exact">Exact Phrase</option>
            <option value="intitle">In Title</option>
            <option value="inurl">In URL</option>
            <option value="site">Site</option>
            <option value="exclude_site">Exclude Site</option>
            <option value="filetype">File Type</option>
          </select>

          {operatorRows.length > 1 && (
            <button
              type="button"
              onClick={() =>
                setOperatorRows((rows) =>
                  rows.filter((item) => item.id !== row.id)
                )
              }
              className="px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
            >
              Remove
            </button>
          )}
        </div>
      ))}
    </div>

    <button
      type="button"
      onClick={() =>
        setOperatorRows((rows) => [
          ...rows,
          {
            id: Date.now(),
            value: "",
            operator: "none",
            join: "AND",
          },
        ])
      }
      className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-800"
      disabled={credits !== null && credits <= 0}
    >
      + Add condition
    </button>

    {buildBroadQuery() && (
      <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="text-xs font-semibold text-gray-500 mb-1">
          Generated search
        </div>

        <code className="text-sm text-gray-800 break-all">
          {buildBroadQuery()}
        </code>
      </div>
    )}
  </div>
)}
        {/* Exclude Input */}
        <div>
          <label className="block text-sm font-semibold mb-2 text-gray-800">
            Exclude domains
            <span className="text-xs font-normal text-gray-400 ml-1">(optional)</span>
          </label>
          <input
            type="text"
            value={exclude}
            onChange={(e) => setExclude(e.target.value)}
            className="w-full border border-gray-200 px-4 py-3.5 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400 transition text-gray-800 placeholder-gray-400"
            placeholder="linkedin.com, reddit.com, example.com"
            disabled={credits !== null && credits <= 0}
          />

          {/* Download + Upload + History */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Download Template */}
              <button
                type="button"
                onClick={() => {
                  const header = ["domain"];
                  const blob = new Blob([header.join(",") + "\r\nexample.com"], {
                    type: "text/csv",
                  });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = "exclude_template.csv";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(a.href);
                }}
                className="px-4 py-2 rounded-lg border border-blue-200 text-blue-700 text-sm font-medium hover:bg-blue-50 transition"
              >
                Download Template
              </button>

              {/* Upload File */}
              <input
                type="file"
                accept=".csv"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const text = await file.text();
                  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
                  const rawDomains = lines.filter((d) => d.toLowerCase() !== "domain");
                  const domains = rawDomains.map(cleanDomain).filter(Boolean).slice(0, 500);
                  const current = exclude ? exclude.split(",").map(cleanDomain).filter(Boolean) : [];
                  const all = Array.from(new Set([...current, ...domains]));
                  setExclude(all.join(","));
                }}
                className="text-sm"
                disabled={credits !== null && credits <= 0}
              />
            </div>

            {/* View History */}
            <div className="w-full sm:w-auto">
              <a
                href="/history"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center px-4 py-2 rounded-lg text-sm font-medium transition bg-blue-600 text-white hover:bg-blue-700"
              >
                View History
              </a>
            </div>
          </div>
        </div>

        {/* Tip Link */}
        <div className="text-sm text-blue-700">
          Don’t know what to do?{" "}
          <Link to="/search-operators" className="underline hover:text-blue-900 font-medium">
            Read this.
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={
            loading ||
            bulkOutreachLoading ||
            (credits !== null && credits <= 0)
          }
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3.5 rounded-xl font-semibold shadow transition-all duration-200 focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50"
        >
          {loading ? <Loader2 className="animate-spin w-5 h-5" /> : null}
          {loading ? "Searching..." : "Search"}
        </button>
      </form>
    </div>

    {/* ─────────── RESULTS SECTION ─────────── */}






      {/* Error Message */}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 text-center">
          {error}
        </div>
      )}

      {/* Results Count */}
      {results.length > 0 && (
        <div className="mb-4 font-semibold text-blue-800 text-lg">
          {`Showing ${startIndex}–${endIndex} results`}
        </div>
      )}

      {/* Download / Copy */}
      {results.length > 0 && (
        <div className="mb-4 flex gap-2">
<button
  onClick={downloadCSV}
  disabled={loading || results.length === 0 || bulkOutreachLoading}
  className="flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700 transition disabled:cursor-not-allowed disabled:opacity-60"
  type="button"
>
            <Download className="w-4 h-4" /> Download This Page
          </button>
<button
  onClick={copyAllUrls}
  disabled={loading || !results.length || bulkOutreachLoading}
  className="flex items-center gap-1 bg-yellow-500 text-white px-4 py-2 rounded-lg font-bold hover:bg-yellow-600 transition disabled:cursor-not-allowed disabled:opacity-60"
  type="button"
>
            <Copy className="w-4 h-4" /> Copy This Page Data
          </button>
          <button
  onClick={handleFindAllOutreach}
disabled={
  loading ||
  results.length === 0 ||
  bulkOutreachLoading ||
  Object.keys(outreachLoading).length > 0
}
  className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 transition disabled:cursor-not-allowed disabled:opacity-60"
  type="button"
>
  {bulkOutreachLoading ? (
    <>
      <Loader2 className="w-4 h-4 animate-spin" />
      Finding Outreach
    </>
  ) : (
    <>Find Outreach for All</>
  )}
</button>
        </div>
      )}

      {/* Toast */}
      {showCopied && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-5 py-2 rounded shadow-lg font-semibold z-50">
          Copied!
        </div>
      )}

      {/* Pagination Top */}
      {results.length > 0 && (
        <div className="flex justify-center items-center gap-4 my-4">
<button
  onClick={() => setPage((p) => Math.max(1, p - 1))}
  disabled={
  loading ||
  bulkOutreachLoading ||
  page === 1 ||
  (isSmartMode && !hasPrev)
}
  className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
>
  <ChevronLeft className="inline w-4 h-4" /> Previous
</button>

<span className="font-bold text-blue-800">
  Page {page}{!isSmartMode && ` of ${MAX_PAGES}`}
</span>

<button
  onClick={() => setPage((p) => p + 1)}
disabled={
  loading ||
  bulkOutreachLoading ||
  credits === 0 ||
  (isSmartMode ? !hasNext : page >= MAX_PAGES)
}

  className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
>
  Next <ChevronRight className="inline w-4 h-4" />
</button>

        </div>
      )}

{/* Results Table / Cards */}
{results.length > 0 && (
  <>
{/* Desktop Results */}
{!isMobile && (
  <div className="mt-5 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[34%]" />
          <col className="w-[18%]" />
          <col className="w-[32%]" />
          <col className="w-[16%]" />
        </colgroup>

        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Website
            </th>

            <th className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
              Metrics
            </th>

            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Outreach
            </th>

            <th className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
              Prospects
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
          {results.map((site, idx) => {
            const domain = toRootDomain(site.url) || site.url;
            const isExpanded = Boolean(expandedOutreach[domain]);

            const emails = site.emails || [];
            const emailContacts =
              site.email_contacts?.length
                ? site.email_contacts
                : emails.map((email) => ({
                    email,
                    type: "general",
                    source_url: "",
                    source_type: null,
                  }));

           const visibleEmailContacts = isExpanded
              ? emailContacts
              : emailContacts.slice(0, 2);

            const hiddenEmailCount = Math.max(
              emailContacts.length - visibleEmailContacts.length,
              0
            );

            const emailTypeLabels: Record<string, string> = {
              editorial: "Editorial",
              advertising: "Advertising",
              support: "Support",
              general: "General",
              personal: "Personal",
              other: "Other",
               };

            const emailSourceLabels: Record<string, string> = {
              write_for_us: "Write For Us",
              advertise: "Advertising",
              partnership: "Partnership",
              contact: "Contact Page",
              homepage: "Homepage",
                };

            const outreachLabels: Record<string, string> = {
              contact: "Contact Page",
              write_for_us: "Write For Us",
              advertise: "Advertising",
            };

            return (
              <tr
                key={`${site.url}-${idx}`}
                className="align-top transition-colors hover:bg-gray-50/70"
              >
                {/* Website */}
                <td className="px-5 py-5">
                  <div className="flex flex-col gap-2">
                    <div
                      className="line-clamp-2 text-sm font-semibold leading-5 text-gray-900"
                      title={site.title}
                    >
                      {site.title || "Untitled result"}
                    </div>

                    <div className="text-xs font-medium text-gray-500">
                      {domain}
                    </div>

                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-fit items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      View article
                      <span aria-hidden="true">↗</span>
                    </a>
                  </div>
                </td>

                {/* Metrics */}
                <td className="px-4 py-5">
                  <div className="flex items-start justify-center gap-3">
                    <div className="min-w-[44px] text-center">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        DA
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {site.da ?? "—"}
                      </div>
                    </div>

                    <div className="min-w-[44px] border-l border-gray-200 text-center">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        PA
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {site.pa ?? "—"}
                      </div>
                    </div>

                    <div className="min-w-[50px] border-l border-gray-200 text-center">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Spam
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-800">
                        {site.spam_score ?? "—"}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Outreach */}
                <td className="px-5 py-5">
                  {Boolean(outreachLoading[domain]) ? (
  <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                      <Loader2
                        size={16}
                        className="animate-spin text-green-600"
                      />
                      <span>Finding outreach...</span>
                    </div>
                  ) : site.outreach_checked ||
                    site.outreach_pages?.length ||
                    site.emails?.length ||
                    site.has_form ? (
                    <div className="flex flex-col gap-2">

                      {/* Outreach page links */}
                      {site.outreach_pages?.map((page, index) => (
                        <a
                          key={`${page.url}-${index}`}
                          href={page.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50/70 px-3 py-2 transition hover:border-green-300 hover:bg-green-50"
                        >
                          <div className="min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-green-600">
                              Outreach page
                            </div>

                            <div className="truncate text-sm font-medium text-green-800">
                              {outreachLabels[page.type] ||
                                page.type?.replace(/_/g, " ") ||
                                "Outreach Page"}
                            </div>
                          </div>

                          <span className="shrink-0 text-sm text-green-700">
                            ↗
                          </span>
                        </a>
                      ))}

{/* Emails */}
{visibleEmailContacts.map((contact) => (
  <div
    key={contact.email}
    className="flex min-w-0 items-center gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2"
  >
    <span className="shrink-0 text-blue-500">
      ✉
    </span>

    <div className="min-w-0 flex-1">
      <a
        href={`mailto:${contact.email}`}
        title={contact.email}
        className="block truncate text-sm font-medium text-blue-700 hover:underline"
      >
        {contact.email}
      </a>

      <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] font-semibold text-blue-500">
        <span>
          {emailTypeLabels[contact.type] ||
            contact.type ||
            "Email"}
        </span>

        {contact.source_url && (
          <>
            <span>·</span>

            <a
              href={contact.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-700 hover:underline"
            >
              {emailSourceLabels[
                contact.source_type || ""
              ] ||
                contact.source_type?.replace(/_/g, " ") ||
                "Source"}{" "}
              ↗
            </a>
          </>
        )}
      </div>
    </div>
  </div>
))}


{/* Form */}
{site.has_form &&
  (site.form_url ? (
    <a
      href={site.form_url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 rounded-lg border border-purple-100 bg-purple-50/60 px-3 py-2 transition hover:border-purple-300 hover:bg-purple-100/70"
    >
      <span className="text-purple-600">✓</span>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-purple-500">
          Form
        </div>
        <div className="text-sm font-medium text-purple-800">
          Contact form available ↗
        </div>
      </div>
    </a>
  ) : (
    <div className="flex items-center gap-2 rounded-lg border border-purple-100 bg-purple-50/60 px-3 py-2">
      <span className="text-purple-600">✓</span>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-purple-500">
          Form
        </div>
        <div className="text-sm font-medium text-purple-800">
          Contact form available
        </div>
      </div>
    </div>
  ))}

                      {/* Expand/collapse emails */}
                      {emailContacts.length > 2 && (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedOutreach((prev) => ({
                              ...prev,
                              [domain]: !prev[domain],
                            }))
                          }
                          className="w-fit px-1 text-xs font-semibold text-blue-600 transition hover:text-blue-800 hover:underline"
                        >
                          {isExpanded
                            ? "Show fewer emails"
                            : `+ ${hiddenEmailCount} more email${
                                hiddenEmailCount === 1 ? "" : "s"
                              }`}
                        </button>
                      )}

                      {/* Nothing found */}
                      {site.outreach_checked &&
                        !site.outreach_pages?.length &&
                        !site.emails?.length &&
                        !site.has_form && (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                            No outreach details found
                          </div>
                        )}
                    </div>
                  ) : (
<button
  type="button"
  onClick={() => handleFindOutreach(domain)}
  disabled={bulkOutreachLoading}
  className="inline-flex items-center justify-center rounded-lg border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-700 transition hover:border-green-400 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50"
>
  Find Outreach
</button>
                  )}
                </td>

                {/* Prospects */}
                <td className="px-4 py-5 text-center">
                  <button
                    type="button"
                    onClick={() => handleOpenProspects(domain)}
                    className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-white px-4 py-2 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-50"
                  >
                    LinkedIn Prospects
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
)}

    {/* Mobile Cards */}
{/* Mobile Cards */}
{isMobile && (
  <div className="flex flex-col gap-4 mt-4">
    {results.map((site, idx) => (
      <div
        key={idx}
        className={`rounded-xl border border-gray-200 bg-white shadow-sm p-4 hover:shadow-md transition ${
          loading ? "opacity-50" : ""
        }`}
      >
        {/* Title with truncate */}
        <div
          className="font-semibold text-gray-900 mb-1 text-sm truncate"
          title={site.title}
        >
          {site.title}
        </div>

        {/* URL with truncate */}
        <div className="mb-2 text-sm text-blue-600 hover:underline truncate" title={site.url}>
          <a
            href={site.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {site.url}
          </a>
        </div>

        {/* Stats + description */}
        <div className="flex flex-col gap-1 text-xs text-gray-600">
          <div>
            <span className="font-medium">DA:</span> {site.da} |{" "}
            <span className="font-medium">PA:</span> {site.pa} |{" "}
            <span className="font-medium">Spam:</span> {site.spam_score}
          </div>
        </div>

{/* Outreach */}
<div className="mt-3">
  <div className="text-xs font-medium text-gray-700 mb-1">
    Outreach
  </div>

  {Boolean(outreachLoading[toRootDomain(site.url) || site.url]) ? (
    <div className="flex items-center gap-1 text-xs text-gray-500">
      <Loader2 size={14} className="animate-spin" />
      Searching...
    </div>
  ) : site.outreach_pages?.length ||
    site.emails?.length ||
    site.has_form ? (
    <div className="flex flex-col items-start gap-1">

      {site.outreach_pages?.map((p, i) => (
        <a
          key={`${p.type}-${i}`}
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-green-700 hover:underline"
        >
          {p.type === "contact"
            ? "Contact Page"
            : p.type === "write_for_us"
            ? "Write For Us"
            : p.type === "advertise"
            ? "Advertising"
            : p.type.replace(/_/g, " ")}
        </a>
      ))}

      {site.emails?.map((email, i) => (
        <a
          key={`email-${i}`}
          href={`mailto:${email}`}
          className="text-xs text-blue-600 hover:underline break-all"
        >
          {email}
        </a>
      ))}

      {site.has_form && (
        <span className="text-xs text-gray-600">
          Contact form found
        </span>
      )}
    </div>
  ) : site.outreach_checked ? (
    <span className="text-xs text-gray-500">
      No outreach found
    </span>
  ) : (
<button
  type="button"
  onClick={() =>
    handleFindOutreach(toRootDomain(site.url) || site.url)
  }
  disabled={bulkOutreachLoading}
  className="text-xs px-3 py-1 rounded-lg border border-green-200 text-green-700 hover:bg-green-50 transition disabled:cursor-not-allowed disabled:opacity-50"
>
  Find Outreach
</button>
  )}
</div>

        {/* Prospects button */}
        <div className="mt-3">
          <button
            type="button"
            onClick={() =>
              handleOpenProspects(toRootDomain(site.url) || site.url)
            }
            className="text-xs px-3 py-1 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 transition"
          >
            LinkedIn Prospects
          </button>
        </div>
      </div>
    ))}
  </div>
)}

  </>
)}

      {/* Pagination Bottom */}
      {results.length > 0 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
disabled={
  page === 1 ||
  loading ||
  bulkOutreachLoading ||
  (isSmartMode && !hasPrev)
}            className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
          >
            <ChevronLeft className="inline w-4 h-4" /> Previous
          </button>

<span className="font-bold text-blue-800">
  Page {page}{!isSmartMode && ` of ${MAX_PAGES}`}
</span>


          <button
            onClick={() => setPage((p) => Math.min(MAX_PAGES, p + 1))}
disabled={
  loading ||
  bulkOutreachLoading ||
  credits === 0 ||
  (isSmartMode ? !hasNext : page >= MAX_PAGES)
}


            className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
          >
            Next <ChevronRight className="inline w-4 h-4" />
          </button>
        </div>
      )}
            {/* Prospects Drawer */}
      <ProspectsDrawer
        open={drawerOpen}
        loading={prospectsLoading}
        error={prospectsError}
        data={prospectsData}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
